import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { LogOut, User, Building2, Users, Briefcase, FileText, BarChart3, CreditCard, BookOpen, TrendingUp } from 'lucide-react';
import { toast } from '../utils/toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checkingConfig, setCheckingConfig] = useState(true);
  const [hospitalStats, setHospitalStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    // Check if user is authenticated
    let cancelled = false;

    const restoreAndRoute = async () => {
      const token = localStorage.getItem('token');
      let userData = localStorage.getItem('user');
      
      if (!token) {
        toast.error('Please sign in to access the dashboard.');
        navigate('/signin');
        return;
      }

      if (!userData) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          userData = JSON.stringify(response.data);
          localStorage.setItem('user', userData);
        } catch (error) {
          toast.error('Please sign in to access the dashboard.');
          navigate('/signin');
          return;
        }
      }

      if (cancelled) return;

      try {
        const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      
      // Redirect Staff to their dashboard
      if (parsedUser.role === 'Staff') {
        navigate(parsedUser.is_reporting_manager ? '/manager/landing' : '/staff/dashboard');
        return;
      }
      
      // Redirect Manager and Reporting Manager to their landing page
      if (parsedUser.role === 'Manager' || parsedUser.role === 'Reporting Manager' || parsedUser.role === 'ReportingManager') {
        navigate('/manager/landing');
        return;
      }
      
      // Redirect Super Admin, Exlva Admin, and Admin to the new sidebar dashboard
      if (['Super Admin', 'Exlva Admin', 'ExlvaAdmin', 'Admin'].includes(parsedUser.role)) {
        navigate('/super-admin');
        return;
      }
      
      // Redirect ExlvaAdminLite to Training Chapters
      if (parsedUser.role === 'ExlvaAdminLite') {
        navigate('/training');
        return;
      }
      
      // Redirect Hospital Admin - check setup status first
      if (parsedUser.role === 'Hospital Admin') {
        checkHospitalSetup(token);
        return;
      }
      
      setCheckingConfig(false);
      } catch (error) {
        console.error('Error parsing user data:', error);
        navigate('/signin');
      }
    };

    restoreAndRoute();
    return () => {
      cancelled = true;
    };
  }, [navigate]);
  

  const checkHospitalSetup = async (token) => {
    try {
      const response = await axios.get(`${API}/hospital/setup-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data.setup_completed) {
        // Redirect to setup page on first login
        toast.info('Welcome! Please complete your initial setup');
        navigate('/hospital/setup');
      } else {
        // Setup is complete, go to reports dashboard
        navigate('/hospital/reports');
      }
    } catch (error) {
      console.error('Failed to check setup status:', error);
      // On error, go to reports dashboard anyway
      navigate('/hospital/reports');
    }
  };
  

  const checkConfiguration = async (token) => {
    try {
      const response = await axios.get(`${API}/hospital/configuration/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.data.is_configured) {
        // Redirect to self-configuration page
        toast.info('Please complete your hospital configuration');
        navigate('/hospital/self-configuration');
      } else {
        // Fetch hospital statistics if configured
        fetchHospitalStats(token);
      }
    } catch (error) {
      console.error('Failed to check configuration status');
    } finally {
      setCheckingConfig(false);
    }
  };
  
  const fetchHospitalStats = async (token) => {
    try {
      const [employeesRes, departmentsRes, subscriptionRes] = await Promise.all([
        axios.get(`${API}/hospital-config/employees`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/hospital-config/departments`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subscriptions/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const employees = employeesRes.data.employees || [];
      const departments = departmentsRes.data.departments || [];
      const subscription = subscriptionRes.data.subscription || {};
      
      setHospitalStats({
        totalEmployees: employees.length,
        activeEmployees: employees.filter(e => e.is_active).length,
        totalDepartments: departments.length,
        planName: subscription.plan_name || 'No Plan',
        totalLicenses: subscription.total_licenses || 0,
        usedLicenses: employees.length,
        subscriptionStatus: subscription.status || 'inactive'
      });
    } catch (error) {
      console.error('Failed to fetch hospital stats:', error);
      setHospitalStats({
        totalEmployees: 0,
        activeEmployees: 0,
        totalDepartments: 0,
        planName: 'No Plan',
        totalLicenses: 0,
        usedLicenses: 0,
        subscriptionStatus: 'inactive'
      });
    } finally {
      setLoadingStats(false);
    }
  };

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('token');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      if (token) {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout API error:', error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('session_id');
    navigate('/signin');
  };

  const hasCRMAccess = () => {
    const crmRoles = ['Super Admin', 'CRM Officer', 'CRM Associate'];
    return crmRoles.includes(user?.role);
  };

  const hasTrainingAccess = () => {
    const trainingRoles = ['Super Admin', 'Exlva Admin', 'Hospital Admin'];
    return trainingRoles.includes(user?.role);
  };

  if (!user || checkingConfig) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // Hospital Admin uses AdminLayout with sidebar
  if (user.role === 'Hospital Admin') {
    return (
      <AdminLayout>
        <div className="p-8">
          <h1 className="text-2xl font-bold text-gray-900 mb-8">DASHBOARD</h1>
          
          {loadingStats ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : (
            <>
              {/* Stats Cards Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {/* Total Employees Card */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                     onClick={() => navigate('/hospital/staff-management')}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-blue-700 mb-2">Total Employees</p>
                      <h3 className="text-4xl font-bold text-blue-900">{hospitalStats?.totalEmployees || 0}</h3>
                    </div>
                    <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-700" />
                    </div>
                  </div>
                </div>

                {/* Active Employees Card */}
                <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                     onClick={() => navigate('/hospital/staff-accounts')}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-green-700 mb-2">Active Staff</p>
                      <h3 className="text-4xl font-bold text-green-900">{hospitalStats?.activeEmployees || 0}</h3>
                    </div>
                    <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-green-700" />
                    </div>
                  </div>
                </div>

                {/* Departments Card */}
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                     onClick={() => navigate('/hospital/staff-management')}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-purple-700 mb-2">Departments</p>
                      <h3 className="text-4xl font-bold text-purple-900">{hospitalStats?.totalDepartments || 0}</h3>
                    </div>
                    <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-purple-700" />
                    </div>
                  </div>
                </div>

                {/* License Usage Card */}
                <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer"
                     onClick={() => navigate('/subscription')}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-yellow-700 mb-2">License Usage</p>
                      <h3 className="text-4xl font-bold text-yellow-900">
                        {hospitalStats?.usedLicenses || 0}/{hospitalStats?.totalLicenses || 0}
                      </h3>
                    </div>
                    <div className="w-12 h-12 bg-yellow-200 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-yellow-700" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Subscription Status Card - Full Width */}
              <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow mb-8">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm font-medium text-teal-700 mb-2">Current Subscription Plan</p>
                    <h3 className="text-[28px] font-bold text-teal-900">{hospitalStats?.planName || 'No Active Plan'}</h3>
                    <p className="text-sm text-teal-700 mt-1">
                      Status: <span className="font-semibold capitalize">{hospitalStats?.subscriptionStatus || 'N/A'}</span>
                    </p>
                  </div>
                  <Button 
                    onClick={() => navigate('/subscription')}
                    className="bg-teal-600 hover:bg-teal-700 text-white"
                  >
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Manage Subscription
                  </Button>
                </div>
              </div>

              {/* Quick Actions Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Training Chapters Card */}
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                      <BookOpen className="w-6 h-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Training Chapters</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Create and manage training materials for your staff
                  </p>
                  <Button
                    onClick={() => navigate('/training')}
                    className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90"
                  >
                    Access Training
                  </Button>
                </div>

                {/* Staff Management / Default Org Workflow Card */}
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      {user && ['Super Admin', 'Exlva Admin', 'Admin'].includes(user.role) 
                        ? 'Default Org Workflow' 
                        : 'Staff Management'}
                    </h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Manage departments, job roles, and employees
                  </p>
                  <Button
                    onClick={() => navigate('/hospital/staff-management')}
                    className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90"
                  >
                    {user && ['Super Admin', 'Exlva Admin', 'Admin'].includes(user.role) 
                      ? 'Manage Workflow' 
                      : 'Manage Staff'}
                  </Button>
                </div>

                {/* Reports & Analytics Card */}
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                      <BarChart3 className="w-6 h-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Reports & Analytics</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    View comprehensive reports and staff performance
                  </p>
                  <Button
                    onClick={() => navigate('/hospital/reports')}
                    className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90"
                  >
                    View Reports
                  </Button>
                </div>

                {/* Chapter Assignment Card */}
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                      <Briefcase className="w-6 h-6 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Chapter Assignment</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Assign training chapters to staff and departments
                  </p>
                  <Button
                    onClick={() => navigate('/hospital/topic-assignment')}
                    className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90"
                  >
                    Assign Chapters
                  </Button>
                </div>

                {/* Staff Accounts Card */}
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-4">
                      <User className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Staff Accounts</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Create and manage staff user accounts
                  </p>
                  <Button
                    onClick={() => navigate('/hospital/staff-accounts')}
                    className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90"
                  >
                    Manage Accounts
                  </Button>
                </div>

                {/* Offline Attendance Card */}
                <div className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                      <FileText className="w-6 h-6 text-teal-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">Offline Attendance</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload attendance records from offline sessions
                  </p>
                  <Button
                    onClick={() => navigate('/hospital/attendance-upload')}
                    className="w-full bg-[#0056B3] hover:bg-[#0056B3]/90"
                  >
                    Upload Attendance
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-8">DASHBOARD</h1>
        
        {/* Welcome Subchapter */}
        <div className="bg-white rounded-2xl p-8 shadow-xl card-shadow mb-8 fade-in">
          <h2 className="text-[28px] font-semibold mb-4" style={{ color: '#0056B3' }}>
            Welcome, {user.full_name}!
          </h2>
          <p className="text-gray-600 text-lg">
            You are now signed in to your Eptral.Hospital dashboard.
          </p>
        </div>

        {/* User Info Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-2xl p-6 shadow-lg card-shadow slide-in-left" data-testid="user-info-card">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-[#0056B3]/10 rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-[#0056B3]" />
              </div>
              <h2 className="text-[18px] font-semibold" style={{ color: '#0056B3' }}>User Information</h2>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Full Name</p>
                <p className="text-base font-medium text-gray-800" data-testid="user-fullname">{user.full_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Email</p>
                <p className="text-base font-medium text-gray-800" data-testid="user-email">{user.email}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Role</p>
                <p className="text-base font-medium text-gray-800" data-testid="user-role">{user.role}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg card-shadow slide-in-right" data-testid="institution-info-card">
            <div className="flex items-center space-x-4 mb-4">
              <div className="w-12 h-12 bg-[#00AEEF]/10 rounded-full flex items-center justify-center">
                <Building2 className="w-6 h-6 text-[#00AEEF]" />
              </div>
              <h2 className="text-[18px] font-semibold" style={{ color: '#0056B3' }}>Institution Details</h2>
            </div>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Institution Name</p>
                <p className="text-base font-medium text-gray-800" data-testid="institution-name">{user.institution_name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">User ID</p>
                <p className="text-base font-medium text-gray-800" data-testid="user-id">{user.id}</p>
              </div>
            </div>
          </div>
        </div>

        {/* CRM Access Card (for authorized users) */}
        {hasCRMAccess() && (
          <div className="bg-white rounded-2xl p-8 shadow-lg card-shadow mb-8">
            <h2 className="text-[22px] font-semibold mb-4" style={{ color: '#0056B3' }}>CRM Management</h2>
            <p className="text-gray-600 mb-6">
              Access lead management, track hospital engagements, and manage follow-ups.
            </p>
            <Button
              data-testid="access-crm-btn"
              onClick={() => navigate('/crm')}
              className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
            >
              Access CRM Dashboard
            </Button>
          </div>
        )}

        {/* Training Chapters Management Card (for authorized users) */}
        {hasTrainingAccess() && (
          <div className="bg-white rounded-2xl p-8 shadow-lg card-shadow mb-8">
            <h2 className="text-[22px] font-semibold mb-4" style={{ color: '#0056B3' }}>Training Chapters Management</h2>
            <p className="text-gray-600 mb-6">
              Create, manage, and publish training chapters with multilingual support for hospital staff.
            </p>
            <Button
              data-testid="access-training-btn"
              onClick={() => navigate('/training')}
              className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
            >
              Access Training Chapters
            </Button>
          </div>
        )}

        {/* Subscription Management Card (for Hospital Admin and Exlva Admin) */}
        {user && (user.role === 'Hospital Admin' || user.role === 'Exlva Admin') && (
          <div className="bg-white rounded-2xl p-8 shadow-lg card-shadow mb-8">
            <h2 className="text-[22px] font-semibold mb-4" style={{ color: '#0056B3' }}>Subscription & Payment Management</h2>
            <p className="text-gray-600 mb-6">
              {user.role === 'Hospital Admin' 
                ? 'Manage your subscription plans, view invoices, track license usage, and handle renewals.'
                : 'Manage all hospital subscriptions, create new subscriptions, and track payments across hospitals.'}
            </p>
            <Button
              onClick={() => navigate(user.role === 'Exlva Admin' ? '/subscriptions/manage' : '/subscription')}
              className="bg-[#00AEEF] hover:bg-[#00AEEF]/90 text-white"
            >
              {user.role === 'Exlva Admin' ? 'Manage All Subscriptions' : 'Manage Subscription'}
            </Button>
          </div>
        )}
        
        {/* Email Template Management (for Exlva Admin only) */}
        {user && (user.role === 'Exlva Admin' || user.role === 'Admin' || user.role === 'Super Admin') && (
          <div className="bg-white rounded-2xl p-8 shadow-lg card-shadow mb-8">
            <h2 className="text-[22px] font-semibold mb-4" style={{ color: '#0056B3' }}>Email Template Management</h2>
            <p className="text-gray-600 mb-6">
              Create, edit, and manage email notification templates for all user roles and categories.
            </p>
            <Button
              onClick={() => navigate('/admin/email-templates')}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              Manage Email Templates
            </Button>
          </div>
        )}
        
        {/* Design System Reference (for Exlva Admin only) */}
        {user && (user.role === 'Exlva Admin' || user.role === 'Admin' || user.role === 'Super Admin') && (
          <div className="bg-white rounded-2xl p-8 shadow-lg card-shadow mb-8">
            <h2 className="text-[22px] font-semibold mb-4" style={{ color: '#0056B3' }}>Design System Reference</h2>
            <p className="text-gray-600 mb-6">
              View comprehensive design guidelines, brand identity, components, and UI framework standards.
            </p>
            <Button
              onClick={() => navigate('/admin/design-system')}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              View Design System
            </Button>
          </div>
        )}
        
        {/* Hospital Configuration & Reporting (for Hospital Admin only) */}
        {user && user.role === 'Hospital Admin' && (
          <div className="bg-white rounded-2xl p-8 shadow-lg card-shadow mb-8">
            <h2 className="text-[22px] font-semibold mb-4" style={{ color: '#0056B3' }}>Hospital Configuration & Reporting</h2>
            <p className="text-gray-600 mb-6">
              Manage staff, departments, job roles, upload offline attendance, and access comprehensive reports.
            </p>
            <div className="flex space-x-4">
              <Button
                onClick={() => navigate('/hospital/staff-management')}
                variant="outline"
                className="border-[#0056B3] text-[#0056B3]"
              >
                Staff Management
              </Button>
              <Button
                onClick={() => navigate('/hospital/reports')}
                className="bg-[#0056B3] hover:bg-[#0056B3]/90 text-white"
              >
                View Reports
              </Button>
            </div>
          </div>
        )}
        
        {/* Staff Dashboard Management (for Hospital Admin only) */}
        {user && user.role === 'Hospital Admin' && (
          <div className="bg-white rounded-2xl p-8 shadow-lg card-shadow mb-8">
            <h2 className="text-[22px] font-semibold mb-4" style={{ color: '#0056B3' }}>Staff Dashboard Management</h2>
            <p className="text-gray-600 mb-6">
              Create staff accounts, assign training chapters, configure leaderboard settings, and manage reporting hierarchies.
            </p>
            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={() => navigate('/hospital/staff-accounts')}
                variant="outline"
                className="border-[#0056B3] text-[#0056B3]"
              >
                Staff Accounts
              </Button>
              <Button
                onClick={() => navigate('/hospital/topic-assignment')}
                variant="outline"
                className="border-[#0056B3] text-[#0056B3]"
              >
                Chapter Assignment
              </Button>
              <Button
                onClick={() => navigate('/hospital/leaderboard-config')}
                variant="outline"
                className="border-[#0056B3] text-[#0056B3]"
              >
                Leaderboard Config
              </Button>
            </div>
          </div>
        )}

        {/* Module Coming Soon */}
        <div className="bg-gradient-to-br from-[#0056B3] to-[#00AEEF] rounded-2xl p-8 text-center shadow-2xl">
          <h2 className="text-[22px] font-semibold text-white mb-4">
            Training Management Modules Coming Soon
          </h2>
          <p className="text-lg text-white/90 mb-6">
            Additional features like staff management, training schedules, and compliance tracking will be available here.
          </p>
          <div className="inline-block bg-white/20 backdrop-blur-sm rounded-lg px-6 py-3">
            <p className="text-white font-medium">Stay tuned for updates!</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;
